import { apiClient } from './client';
import type { ChatMode } from '../chat/types';
import type { QuestionAnswer, TemplateSection } from '../chat/draft/types';

/**
 * All message-sending logic. Mirrors the web app's payload shapes but
 * differentiates the Word add-in via:
 *   - `metadata.source = 'word_addon'`        (passed to AI agent + backend logs)
 *   - `chat_type: 'word_edit'`                (new chat type, distinct from 'draft')
 *   - No `POST /api/v1/draft` call for edits   (stateless — no draft DB rows)
 *
 * The backend already accepts `chat_type: "word_edit"` — CreateChatRequest
 * doesn't enforce a strict enum.
 */

interface CommonMeta {
  userId: string;
}

interface EnsureChatArgs {
  chatId: string;
  chatType: 'research' | 'word_edit' | 'draft';
  name: string;
  caseId?: string | null;
  /**
   * Extra metadata to merge into the chat row at creation time. The backend
   * uses jsonb `||` (top-level merge) for all later updates, so values set
   * here are preserved through every lifecycle phase.
   *
   * `source: 'word_addon'` is always stamped automatically — no need to
   * pass it through.
   */
  metadata?: Record<string, unknown>;
}

/**
 * Create a chat row if it doesn't exist yet. Backend upserts (ON CONFLICT
 * DO NOTHING), so calling twice for the same chat_id is safe.
 */
export async function ensureChatExists({
  chatId,
  chatType,
  name,
  caseId,
  metadata,
}: EnsureChatArgs): Promise<void> {
  await apiClient.post('/api/v1/chat', {
    chat_id: chatId,
    chat_type: chatType,
    name,
    case_id: caseId ?? null,
    is_pinned: false,
    metadata: { source: 'word_addon', ...(metadata ?? {}) },
  });
}

export interface SendResearchArgs extends CommonMeta {
  chatId: string;
  featureId: string;     // typically a UUID used as message-level id (= research_id row)
  content: string;
  quotedContent?: string;
  /** "msg" = deep research, "llm_direct" = fast research */
  subType?: 'msg' | 'llm_direct';
}

export async function sendResearchMessage(args: SendResearchArgs): Promise<void> {
  const { chatId, featureId, content, quotedContent, subType = 'msg', userId } = args;
  await apiClient.post('/api/v1/chat/messages', {
    user_id: userId,
    thread_id: chatId,
    case_id: '',
    type: 'research',
    sub_type: subType,
    content,
    feature_id: featureId,
    metadata: {
      source: 'word_addon',
      ...(quotedContent ? { quoted_content: quotedContent } : {}),
    },
    data: [
      {
        type: 'chats',
        operation: 'update',
        data: { chat_id: chatId, case_id: '', status: 'pending' },
      },
    ],
  });
}

export interface SendEditArgs extends CommonMeta {
  chatId: string;
  content: string;
  draftS3Key: string;
  quotedContent?: string;
  /** For quote_edit: correlation id that ties SSE response back to the selection */
  changeId?: string;
  /**
   * True if this is the first edit message on a brand-new chat. When true,
   * the payload includes `data:[{type:'draft', op:'insert'}, {type:'draft_versions',
   * op:'insert'}]` so the backend message consumer creates the draft record
   * server-side — same pattern web uses (frontend-prod/app/draft/[id]/page.tsx:340-352).
   *
   * After this lands, plugin-originated edits are regular `chat_type='draft'`
   * rows that the web UI can open in `/draft/edit/[id]`.
   */
  isFirstEdit?: boolean;
  /**
   * Version string for the draft_versions row on first edit. Typically '1'.
   */
  version?: string;
}

/**
 * Send an edit instruction as a `chat_type='draft'` message.
 *
 * Two sub-modes:
 *   - `quote_edit`: user selected text. AI returns `metadata.edited_text` +
 *     `change_id`. Frontend applies as a tracked change.
 *   - `full_edit`: no selection. AI writes a new DOCX to S3, returns s3_key.
 *
 * On `isFirstEdit === true`, we also include the `draft` + `draft_versions`
 * insert data operations so the backend creates the matching rows — this
 * is what makes a plugin edit session visible + resumable in the web UI's
 * /draft/edit page (same contract as web's draft flow).
 */
export async function sendEditMessage(args: SendEditArgs): Promise<void> {
  const {
    chatId,
    content,
    draftS3Key,
    quotedContent,
    changeId,
    userId,
    isFirstEdit = false,
    version = '1',
  } = args;
  const subType = quotedContent ? 'quote_edit' : 'full_edit';

  const data: Array<Record<string, unknown>> = [
    {
      type: 'chats',
      operation: 'update',
      data: { chat_id: chatId, case_id: '', status: 'pending' },
    },
  ];

  if (isFirstEdit) {
    // Create the draft row on first edit. Mirrors web's generate_template
    // data[] — except we go straight to edit because the user already has
    // a document in Word (no template/QnA phase).
    data.unshift(
      {
        type: 'draft',
        operation: 'insert',
        data: {
          draft_id: chatId,
          user_id: userId,
          case_id: '',
          chat_id: chatId,
          draft_query: content,
          latest_version: version,
        },
      },
      {
        type: 'draft_versions',
        operation: 'insert',
        data: {
          version_id: `${chatId}_${version}`,
          draft_id: chatId,
          version,
          s3_link: draftS3Key,
        },
      },
    );
  }

  await apiClient.post('/api/v1/chat/messages', {
    user_id: userId,
    thread_id: chatId,
    case_id: '',
    type: 'draft',
    sub_type: subType,
    content,
    feature_id: chatId,
    metadata: {
      source: 'word_addon',
      draft_s3_key: draftS3Key,
      latest_version: version,
      ...(quotedContent ? { quoted_content: quotedContent } : {}),
      ...(changeId ? { change_id: changeId } : {}),
    },
    data,
  });
}

export async function stopExecution(chatId: string): Promise<void> {
  try {
    await apiClient.post('/api/v1/chat/stop', { chat_id: chatId });
  } catch {
    // Non-fatal; the request may have already completed.
  }
}

// --------------------------------------------------------------------------
// Draft mode (experimental, gated by FEATURES.draftMode)
//
// Three-phase chat conversation shared with the web app's Draft flow. Payload
// shapes mirror frontend-prod's draftSlice.buildGenerateDraftPayload etc., so
// the Python agent at agents/draft/ routes them identically. The only add-in
// difference: `metadata.source = 'word_addon'` so logs + future rate limiting
// can distinguish client origins.
// --------------------------------------------------------------------------

interface DraftCommonArgs extends CommonMeta {
  /**
   * For draft-mode calls, chat_id === feature_id === draft_id (all one UUID).
   * Matches frontend-prod's draftSlice/buildGenerateDraftPayload where the
   * URL's draft id is used for all three keys.
   */
  chatId: string;
}

/**
 * Attachments block every draft SSE message carries. Plugin v1 doesn't
 * surface an attachments picker (skipped per design), but the schema
 * requires the keys be present with empty arrays — web always sends it.
 */
const EMPTY_ATTACHMENTS = {
  research_ids: [] as string[],
  draft_links: [] as string[],
  doc_links: [] as unknown[],
  meeting_links: [] as unknown[],
};

export interface SendDraftTemplateArgs extends DraftCommonArgs {
  /** The user's natural-language brief from the prompt phase */
  prompt: string;
  /** Short name (brief prefix) the chat row is titled with. */
  name: string;
  /**
   * F1 — Auto/Instant mode. When 'instant', the backend skips QnA and goes
   * brief → DOCX directly. Sent at the TOP level of the payload (NOT in
   * metadata) to mirror frontend-prod/app/draft/[id]/page.tsx:360.
   * Default 'interactive' is omitted from the wire payload entirely so
   * existing behaviour is preserved when callers don't pass anything.
   */
  draftExperience?: 'interactive' | 'instant';
  /**
   * F3 — Concise vs Detailed. Sent at TOP level of the payload (matches
   * frontend-prod's `mode` field at `app/draft/[id]/page.tsx:364, 884`).
   * Backend maps to `state["processing_mode"]` in the AI agent. Optional;
   * omitted when the caller doesn't override the default.
   */
  mode?: 'speed' | 'accuracy';
}

/**
 * Phase 1 — asks the agent to propose a section structure for the brief.
 *
 * Payload shape is 1:1 with frontend-prod/app/draft/[id]/page.tsx:327-368:
 *   - `data[]` carries `{type:'draft', operation:'insert', ...}` — the
 *     backend message consumer inserts the draft row server-side, so
 *     later `UPDATE` ops land on an existing row.
 *   - `should_show: false` mirrors web (these phase messages aren't rendered
 *     in the chat scroll).
 *
 * Plugin-specific addition: `metadata.source = 'word_addon'` so the backend
 * rate-limit middleware + structured logs can distinguish plugin traffic.
 */
export async function sendDraftTemplate(args: SendDraftTemplateArgs): Promise<void> {
  const { userId, chatId, prompt, name, draftExperience, mode } = args;
  // Top-level draft_experience matches web's payload shape. Only include
  // when 'instant' so default-Guided payloads stay byte-identical to v0.
  const experiencePayload =
    draftExperience === 'instant' ? { draft_experience: 'instant' as const } : {};
  // F3 — only include `mode` when caller specified. Backend default = 'speed'.
  const modePayload = mode ? { mode } : {};
  await apiClient.post('/api/v1/chat/messages', {
    thread_id: chatId,
    user_id: userId,
    case_id: '',
    feature_id: chatId, // same UUID, matches web
    type: 'draft',
    sub_type: 'generate_template',
    content: prompt,
    should_show: false,
    name,
    ...experiencePayload,
    ...modePayload,
    metadata: { source: 'word_addon' },
    attachments: EMPTY_ATTACHMENTS,
    data: [
      {
        type: 'draft',
        operation: 'insert',
        data: {
          draft_id: chatId,
          user_id: userId,
          case_id: '',
          chat_id: chatId,
          draft_query: prompt,
        },
      },
    ],
  });
}

export interface SendDraftQuestionsArgs extends DraftCommonArgs {
  template: TemplateSection[];
  /** F3 — see SendDraftTemplateArgs.mode for semantics. */
  mode?: 'speed' | 'accuracy';
}

/**
 * Phase 2 — send reviewed template, agent returns QnA list.
 *
 * Matches frontend-prod/app/draft/[id]/page.tsx:402-429: `chat_type:'draft'`
 * at the message level, `status:'pending'` on the payload, `data[]` updates
 * the chat to pending.
 */
export async function sendDraftQuestions(args: SendDraftQuestionsArgs): Promise<void> {
  const { userId, chatId, template, mode } = args;
  const modePayload = mode ? { mode } : {};
  await apiClient.post('/api/v1/chat/messages', {
    thread_id: chatId,
    user_id: userId,
    case_id: '',
    feature_id: chatId,
    type: 'draft',
    sub_type: 'generate_questions',
    chat_type: 'draft',
    content: 'Generate questions for this template',
    should_show: false,
    status: 'pending',
    ...modePayload,
    metadata: {
      source: 'word_addon',
      template: { sections: template.map(serializeSection) },
    },
    data: [
      {
        type: 'chats',
        operation: 'update',
        data: { chat_id: chatId, case_id: '', status: 'pending' },
      },
    ],
  });
}

function serializeSection(s: TemplateSection, i: number): Record<string, unknown> {
  return {
    id: s.id,
    section_number: s.number ?? String(i + 1),
    section_header: s.header,
    description: s.description ?? '',
    must_include: s.mustInclude ?? [],
    generation_guidance: s.guidance ?? '',
    expected_depth: s.expectedDepth ?? 'standard',
    complexity: s.complexity ?? 'standard',
    section_subheaders: s.subheaders.map((sh, j) => ({
      id: sh.id,
      section_number: sh.number ?? `${i + 1}.${j + 1}`,
      title: sh.title,
      description: sh.description ?? '',
    })),
  };
}

export interface SendDraftGenerateArgs extends DraftCommonArgs {
  template: TemplateSection[];
  questions: QuestionAnswer[];
  /** F3 — see SendDraftTemplateArgs.mode for semantics. */
  mode?: 'speed' | 'accuracy';
}

/**
 * Phase 3 — send answered questions, agent generates final DOCX in S3.
 *
 * Matches frontend-prod/app/store/reducers/draftSlice.ts:1095-1148
 * (`buildGenerateDraftPayload`). Key detail: questions/answers are
 * **string arrays** (just the text), not objects — the agent maps them
 * positionally.
 */
export async function sendDraftGenerate(args: SendDraftGenerateArgs): Promise<void> {
  const { userId, chatId, template, questions, mode } = args;
  const questionStrings = questions.map((q) => q.question);
  const answerStrings = questions.map((q) => q.answer ?? '');
  const modePayload = mode ? { mode } : {};
  await apiClient.post('/api/v1/chat/messages', {
    thread_id: chatId,
    user_id: userId,
    case_id: '',
    feature_id: chatId,
    type: 'draft',
    sub_type: 'generate_draft',
    chat_type: 'draft',
    content: 'Generate Final Draft',
    should_show: false,
    ...modePayload,
    attachments: EMPTY_ATTACHMENTS,
    metadata: {
      source: 'word_addon',
      template: { sections: template.map(serializeSection) },
      questions: questionStrings,
      answers: answerStrings,
    },
    data: [
      {
        type: 'chats',
        operation: 'update',
        data: { chat_id: chatId, case_id: '', status: 'pending' },
      },
    ],
  });
}
